import {
  Autocomplete,
  Avatar,
  Button,
  IconButton,
  LinearProgress,
  TextField,
  Tooltip,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { AiOutlineCheck, AiOutlineClose, AiOutlinePlus } from "react-icons/ai";
import { FaRegEdit, FaTrash } from "react-icons/fa";
import { MdOutlinePlaylistAdd, MdOutlineStickyNote2 } from "react-icons/md";
import { RiCheckboxBlankCircleLine, RiCheckboxCircleLine } from "react-icons/ri";
import { toast } from "react-toastify";
import {
  useCreateSubtask,
  useCreateSubtasksBulk,
  useDeleteSubtask,
  useUpdateSubtask,
} from "utils/taskService";
import ConfirmDialog from "components/Modals/ConfirmDialog";
import SubtaskNotes from "components/Tasks/SubtaskNotes";
import {
  formatTaskDate,
  getAvatarColor,
  getSubtaskAssignee,
  getSubtaskDeadlineInfo,
} from "utils/taskUtils";

const toDateInputValue = (date) => {
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().split("T")[0];
};

/** Subtasks default to a week after the parent task was created, so a due date is always tracked. */
const getDefaultSubtaskDeadline = (createdDate) => {
  const parsed = createdDate ? new Date(createdDate) : new Date();
  const due = new Date(Number.isNaN(parsed.getTime()) ? Date.now() : parsed.getTime());
  due.setDate(due.getDate() + 7);
  return toDateInputValue(due);
};

/** Derives a compact, at-a-glance tracking badge for a subtask from its own completed/deadline state. */
const getSubtaskStatusBadge = (subtask) => {
  if (subtask?.completed) {
    return { label: "Done", cls: "bg-app-status-completed-bg text-app-status-completed-text" };
  }
  const info = getSubtaskDeadlineInfo(subtask);
  if (info?.isPassed) {
    return { label: "Overdue", cls: "bg-app-priority-high-bg text-app-priority-high-text" };
  }
  if (info?.isDueSoon) {
    return { label: "Due soon", cls: "bg-app-priority-medium-bg text-app-priority-medium-text" };
  }
  if (subtask?.deadline) {
    return {
      label: formatTaskDate(subtask.deadline),
      cls: "bg-app-surface-muted text-app-text-muted",
    };
  }
  return null;
};

/**
 * Renders + manages a task's checklist. In "draft" mode subtasks live purely in local state
 * (used while creating a brand-new task); in "live" mode every change is persisted immediately
 * against `taskId` via the task API, matching the rest of the module's quick-save UX.
 */
const SubtaskManager = ({
  mode = "draft",
  subtasks = [],
  onDraftChange,
  taskId,
  onLiveChange,
  users = [],
  compact = false,
  canManage = true,
  canAddSubtask = null,
  currentUserEmail = null,
  taskCreatedDate = null,
}) => {
  // Raising a subtask is open to everyone on the task; changing one is scoped to the people who
  // own that row. Draft subtasks have no server-side row yet, so they fall back to canManage.
  const mayAddSubtask = canAddSubtask ?? canManage;
  const rowCanManage = (subtask) =>
    typeof subtask?.canManage === "boolean" ? subtask.canManage : canManage;
  const defaultDeadline = useMemo(
    () => getDefaultSubtaskDeadline(taskCreatedDate),
    [taskCreatedDate]
  );
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [draftTitle, setDraftTitle] = useState("");
  const [draftAssignee, setDraftAssignee] = useState(null);
  const [draftDeadline, setDraftDeadline] = useState(defaultDeadline);
  const [deadlineEdited, setDeadlineEdited] = useState(false);
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [notesIndex, setNotesIndex] = useState(null);
  const [editingTitle, setEditingTitle] = useState({ index: null, value: "" });
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [subtaskPendingDeletion, setSubtaskPendingDeletion] = useState(null);

  const { mutate: createSubtask, isLoading: isCreating } = useCreateSubtask({
    onSuccess: () => onLiveChange?.(),
  });
  const { mutate: createSubtasksBulk, isLoading: isCreatingBulk } = useCreateSubtasksBulk({
    onSettled: () => onLiveChange?.(),
  });
  const { mutate: updateSubtask } = useUpdateSubtask({ onSuccess: () => onLiveChange?.() });
  const { mutate: deleteSubtask } = useDeleteSubtask({ onSuccess: () => onLiveChange?.() });

  // The parent task's created date can arrive after this renders, so keep following the
  // derived default until the user picks their own date.
  useEffect(() => {
    if (!deadlineEdited) setDraftDeadline(defaultDeadline);
  }, [defaultDeadline, deadlineEdited]);

  const done = subtasks.filter((s) => s?.completed).length;
  const total = subtasks.length;
  const percent = total ? Math.round((done / total) * 100) : 0;
  const overdueCount = subtasks.filter(
    (s) => !s?.completed && getSubtaskDeadlineInfo(s)?.isPassed
  ).length;

  const findUser = (userId) => users.find((u) => u.id === userId) || null;

  const addOne = (title, assigneeId, deadline) => {
    if (mode === "draft") {
      onDraftChange?.([
        ...subtasks,
        {
          title,
          assigneeId: assigneeId ?? null,
          assigneeName: findUser(assigneeId)?.name ?? draftAssignee?.name ?? null,
          deadline: deadline || null,
        },
      ]);
    } else if (taskId) {
      createSubtask({
        id: taskId,
        title,
        assigneeId: assigneeId ?? null,
        deadline: deadline || null,
      });
    }
  };

  const resetDraft = () => {
    setDraftTitle("");
    setDraftAssignee(null);
    setDraftDeadline(defaultDeadline);
    setDeadlineEdited(false);
  };

  const closeAdd = () => {
    resetDraft();
    setIsAddOpen(false);
  };

  const handleAddDraft = () => {
    const title = draftTitle.trim();
    if (!title) return;
    addOne(title, draftAssignee?.id, draftDeadline);
    resetDraft();
  };

  const closeBulk = () => {
    setBulkText("");
    setBulkMode(false);
  };

  const handleAddBulk = () => {
    const lines = bulkText
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);
    if (!lines.length) return;
    if (mode === "draft") {
      onDraftChange?.([
        ...subtasks,
        ...lines.map((title) => ({ title, assigneeId: null, assigneeName: null, deadline: null })),
      ]);
      closeBulk();
      return;
    }
    if (!taskId) return;
    // One mutation for the whole batch, so the task list is invalidated once
    // rather than once per line.
    createSubtasksBulk(
      { id: taskId, titles: lines },
      {
        onSuccess: closeBulk,
        onError: (error) => {
          // Keep whatever did not get saved in the box so the user can retry it.
          const remaining = error?.remainingTitles ?? lines;
          setBulkText(remaining.join("\n"));
          toast.error(
            error?.createdCount
              ? `Added ${error.createdCount} of ${lines.length} subtasks — the rest are still in the box.`
              : "Could not add the subtasks. Please try again."
          );
        },
      }
    );
  };

  const handleRemove = () => {
    const { index, subtask } = subtaskPendingDeletion;
    if (mode === "draft") {
      onDraftChange?.(subtasks.filter((_, i) => i !== index));
    } else if (taskId && subtask?.id) {
      deleteSubtask({ id: taskId, subtaskId: subtask.id });
    }
    setSubtaskPendingDeletion(null);
  };

  const handleToggleComplete = (index, subtask) => {
    if (mode === "draft") {
      onDraftChange?.(
        subtasks.map((s, i) => (i === index ? { ...s, completed: !s.completed } : s))
      );
    } else if (taskId && subtask?.id) {
      updateSubtask({ id: taskId, subtaskId: subtask.id, completed: !subtask.completed });
    }
  };

  const handleAssigneeChange = (index, subtask, user) => {
    if (mode === "draft") {
      onDraftChange?.(
        subtasks.map((s, i) =>
          i === index ? { ...s, assigneeId: user?.id ?? null, assigneeName: user?.name ?? null } : s
        )
      );
    } else if (taskId && subtask?.id) {
      updateSubtask({ id: taskId, subtaskId: subtask.id, assigneeId: user?.id ?? null });
    }
  };

  const handleDeadlineChange = (index, subtask, value) => {
    if (mode === "draft") {
      onDraftChange?.(
        subtasks.map((s, i) => (i === index ? { ...s, deadline: value || null } : s))
      );
    } else if (taskId && subtask?.id) {
      updateSubtask({ id: taskId, subtaskId: subtask.id, deadline: value || null });
    }
  };

  const startEditTitle = (index, currentTitle) =>
    setEditingTitle({ index, value: currentTitle || "" });

  const commitEditTitle = (index, subtask) => {
    const value = editingTitle.value.trim();
    setEditingTitle({ index: null, value: "" });
    if (!value || value === subtask.title) return;
    if (mode === "draft") {
      onDraftChange?.(subtasks.map((s, i) => (i === index ? { ...s, title: value } : s)));
    } else if (taskId && subtask?.id) {
      updateSubtask({ id: taskId, subtaskId: subtask.id, title: value });
    }
  };

  return (
    <div>
      {total > 0 && (
        <div className="mb-3">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-app-text-muted">
              {done}/{total} completed
              {overdueCount > 0 && (
                <span
                  className="ml-2 font-medium"
                  style={{ color: "var(--app-priority-high-text)" }}
                >
                  &middot; {overdueCount} overdue
                </span>
              )}
            </span>
            <span className="text-xs text-app-text-muted">{percent}%</span>
          </div>
          <LinearProgress
            variant="determinate"
            value={percent}
            color={overdueCount > 0 ? "error" : "primary"}
            sx={{ height: 6, borderRadius: 3 }}
          />
        </div>
      )}

      <ul className={`space-y-1 ${total ? "mb-3" : "mb-2"}`}>
        {subtasks.map((subtask, index) => {
          const assignee =
            getSubtaskAssignee(subtask) ||
            (subtask.assigneeId ? findUser(subtask.assigneeId) : null);
          const badge = getSubtaskStatusBadge(subtask);
          const deadlineValue = subtask.deadline ? String(subtask.deadline).split("T")[0] : "";
          const isExpanded = expandedIndex === index;
          const isNotesOpen = notesIndex === index;
          const isEditingTitle = editingTitle.index === index;
          const assigneeEmail = subtask.assigneeEmail || assignee?.email || null;
          const canEditRow = rowCanManage(subtask);
          const canToggle =
            canEditRow ||
            (currentUserEmail != null &&
              assigneeEmail != null &&
              assigneeEmail === currentUserEmail);
          return (
            <li
              key={subtask.id ?? `draft-${index}`}
              className="group px-2 py-1.5 rounded-lg hover:bg-app-surface-hover border border-transparent hover:border-app-border"
            >
              <div className="flex items-center gap-2">
                {mode === "live" ? (
                  <Tooltip
                    title={
                      canToggle
                        ? ""
                        : "Only the task's assigner, its assignee, or this subtask's assignee can complete it"
                    }
                  >
                    <span>
                      <IconButton
                        size="small"
                        disabled={!canToggle}
                        onClick={() => handleToggleComplete(index, subtask)}
                      >
                        {subtask.completed ? (
                          <RiCheckboxCircleLine
                            style={{ color: "var(--app-status-completed-text)" }}
                          />
                        ) : (
                          <RiCheckboxBlankCircleLine className="text-app-text-muted" />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                ) : (
                  <span className="w-2 h-2 rounded-full bg-primary flex-shrink-0 ml-1.5" />
                )}
                {isEditingTitle ? (
                  <TextField
                    size="small"
                    variant="standard"
                    autoFocus
                    fullWidth
                    value={editingTitle.value}
                    onChange={(e) => setEditingTitle({ index, value: e.target.value })}
                    onBlur={() => commitEditTitle(index, subtask)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        commitEditTitle(index, subtask);
                      } else if (e.key === "Escape") {
                        setEditingTitle({ index: null, value: "" });
                      }
                    }}
                    sx={{ flex: 1 }}
                  />
                ) : (
                  <span
                    className={`text-sm flex-1 truncate rounded px-1 -mx-1 ${
                      canEditRow ? "cursor-text hover:bg-app-surface-muted" : ""
                    } ${subtask.completed ? "line-through text-app-text-muted" : "text-app-text"}`}
                    title={canEditRow ? "Click to rename" : undefined}
                    onClick={canEditRow ? () => startEditTitle(index, subtask.title) : undefined}
                  >
                    {subtask.title}
                  </span>
                )}
                {badge && (
                  <span
                    className={`text-[11px] font-medium whitespace-nowrap px-1.5 py-0.5 rounded ${badge.cls}`}
                  >
                    {badge.label}
                  </span>
                )}
                {assignee?.name && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-app-text-muted"> {assignee.name} </span>
                    <Avatar
                      sx={{
                        width: 20,
                        height: 20,
                        fontSize: 11,
                        bgcolor: getAvatarColor(assignee.id ?? assignee.name),
                      }}
                    >
                      {assignee.name.charAt(0).toUpperCase()}
                    </Avatar>
                  </div>
                )}

                {canEditRow && (
                  <Tooltip title="Edit assignee / due date">
                    <IconButton
                      size="small"
                      className={
                        isExpanded ? "" : "opacity-0 group-hover:opacity-100 transition-opacity"
                      }
                      onClick={() => setExpandedIndex(isExpanded ? null : index)}
                    >
                      <FaRegEdit className="text-xs text-app-text-secondary" />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Notes">
                  <IconButton
                    size="small"
                    onClick={() => setNotesIndex(isNotesOpen ? null : index)}
                  >
                    <MdOutlineStickyNote2
                      className={`text-sm ${
                        isNotesOpen ? "text-primary" : "text-app-text-secondary"
                      }`}
                    />
                  </IconButton>
                </Tooltip>
                {canEditRow && (
                  <Tooltip title="Remove subtask">
                    <IconButton
                      size="small"
                      className="opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={() => setSubtaskPendingDeletion({ index, subtask })}
                    >
                      <FaTrash className="text-xs text-app-text-secondary" />
                    </IconButton>
                  </Tooltip>
                )}
              </div>

              {isExpanded && (
                <div className="flex flex-wrap items-center gap-2 mt-1.5 pl-9">
                  <TextField
                    size="small"
                    type="date"
                    variant="standard"
                    label="Due date"
                    value={deadlineValue}
                    onChange={(e) => handleDeadlineChange(index, subtask, e.target.value)}
                    InputLabelProps={{ shrink: true, sx: { fontSize: 12 } }}
                    sx={{ width: compact ? 118 : 140 }}
                  />
                  <Autocomplete
                    size="small"
                    options={users}
                    getOptionLabel={(o) => o?.name || ""}
                    isOptionEqualToValue={(o, v) => o?.id === v?.id}
                    value={assignee?.id ? findUser(assignee.id) || assignee : null}
                    onChange={(_, value) => handleAssigneeChange(index, subtask, value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Assignee"
                        variant="standard"
                        InputLabelProps={{ sx: { fontSize: 12 } }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id} className="flex items-center gap-2">
                        <Avatar
                          sx={{
                            width: 20,
                            height: 20,
                            fontSize: 11,
                            bgcolor: getAvatarColor(option.id),
                          }}
                        >
                          {(option.name || "?").charAt(0).toUpperCase()}
                        </Avatar>
                        {option.name}
                      </li>
                    )}
                    sx={{ width: compact ? 150 : 180, flexShrink: 0 }}
                  />
                  <Tooltip title="Done">
                    <IconButton size="small" color="primary" onClick={() => setExpandedIndex(null)}>
                      <AiOutlineCheck />
                    </IconButton>
                  </Tooltip>
                </div>
              )}

              {isNotesOpen &&
                (mode === "live" && subtask?.id ? (
                  <div className="pl-9">
                    <SubtaskNotes taskId={taskId} subtaskId={subtask.id} compact={compact} />
                  </div>
                ) : (
                  <p className="pl-9 mt-2 text-xs text-app-text-muted">
                    Notes can be added once the task is saved.
                  </p>
                ))}
            </li>
          );
        })}
        {subtasks.length === 0 && !bulkMode && (
          <li className="text-sm text-app-text-muted py-2 px-2">
            No subtasks yet — break this down into smaller steps.
          </li>
        )}
      </ul>

      {mayAddSubtask &&
        (bulkMode ? (
          <div className="space-y-2">
            <TextField
              size="small"
              fullWidth
              multiline
              minRows={3}
              autoFocus
              placeholder={
                "One subtask per line, e.g.\nCollect documents\nReview & sign\nSubmit to authority"
              }
              value={bulkText}
              onChange={(e) => setBulkText(e.target.value)}
            />
            <div className="flex items-center gap-2">
              <Button
                size="small"
                variant="contained"
                onClick={handleAddBulk}
                disabled={!bulkText.trim() || isCreatingBulk}
              >
                {isCreatingBulk
                  ? "Adding…"
                  : `Add ${
                      bulkText
                        .split("\n")
                        .map((l) => l.trim())
                        .filter(Boolean).length || ""
                    } subtasks`}
              </Button>
              <Button size="small" onClick={closeBulk} disabled={isCreatingBulk}>
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            {isAddOpen ? (
              <>
                <TextField
                  size="small"
                  autoFocus
                  placeholder="Subtask title"
                  value={draftTitle}
                  onChange={(e) => setDraftTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddDraft();
                    } else if (e.key === "Escape") {
                      closeAdd();
                    }
                  }}
                  sx={{ flex: "1 1 160px", minWidth: 140 }}
                />
                <Tooltip title="Due date — defaults to a week after the task was created">
                  <TextField
                    size="small"
                    type="date"
                    value={draftDeadline}
                    onChange={(e) => {
                      setDeadlineEdited(true);
                      setDraftDeadline(e.target.value);
                    }}
                    sx={{ width: compact ? 132 : 148, flexShrink: 0 }}
                  />
                </Tooltip>
                <Autocomplete
                  size="small"
                  options={users}
                  getOptionLabel={(o) => o?.name || ""}
                  isOptionEqualToValue={(o, v) => o?.id === v?.id}
                  value={draftAssignee}
                  onChange={(_, value) => setDraftAssignee(value)}
                  renderInput={(params) => <TextField {...params} placeholder="Assign to" />}
                  sx={{ width: compact ? 140 : 160, flexShrink: 0 }}
                />
                <Tooltip title="Add subtask">
                  <span>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={handleAddDraft}
                      disabled={!draftTitle.trim() || isCreating}
                    >
                      <AiOutlineCheck />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Cancel">
                  <IconButton size="small" onClick={closeAdd}>
                    <AiOutlineClose className="text-app-text-secondary" />
                  </IconButton>
                </Tooltip>
              </>
            ) : (
              <>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AiOutlinePlus />}
                  onClick={() => setIsAddOpen(true)}
                >
                  Add subtask
                </Button>
                <Tooltip title="Add multiple subtasks at once">
                  <IconButton size="small" onClick={() => setBulkMode(true)}>
                    <MdOutlinePlaylistAdd />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </div>
        ))}

      <ConfirmDialog
        open={subtaskPendingDeletion != null}
        title="Delete subtask?"
        message={`"${
          subtaskPendingDeletion?.subtask?.title || "This subtask"
        }" will be removed from this task. This cannot be undone.`}
        confirmLabel="Delete"
        confirmColor="error"
        onConfirm={handleRemove}
        onCancel={() => setSubtaskPendingDeletion(null)}
      />
    </div>
  );
};

export default SubtaskManager;
